/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React from 'react';

import { jsx, cssMap } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import TeamworkGraphIcon from '@atlaskit/icon-lab/core/teamwork-graph';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { Card } from '@atlaskit/smart-card/card/lazy';
import { SmartLinkSize, SmartLinkTheme } from '@atlaskit/smart-card/enums';
import { TitleBlock } from '@atlaskit/smart-card/flexible/title-block';
import { token } from '@atlaskit/tokens';

import type { SuggestionSource } from '../../../aiSuggestionsPluginType';

const cardUiOptions = {
	clickableContainer: true,
	hideBackground: true,
	hideElevation: true,
	hidePadding: true,
	size: SmartLinkSize.Medium,
	theme: SmartLinkTheme.Black,
};

const styles = cssMap({
	sourcesContainer: {
		overflow: 'visible',
		width: '100%',
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
	},
	sourceLabel: {
		paddingRight: token('space.150'),
		paddingLeft: token('space.150'),
	},
	sourceCard: {
		position: 'relative',
		overflow: 'visible',
		width: '100%',
		boxSizing: 'border-box',
		minWidth: 0,
		minHeight: token('space.250'),
		paddingRight: token('space.150'),
		paddingLeft: token('space.150'),
	},
	// Keep the card mounted (so its lazy IntersectionObserver still fires and it
	// resolves) but hidden, so the raw URL doesn't flash before it resolves.
	cardLoading: {
		visibility: 'hidden',
	},
	// Skeleton overlay shown while the card resolves. Absolutely positioned so it
	// sits on top of the (hidden) card without adding to the reserved height.
	skeletonOverlay: {
		position: 'absolute',
		inset: token('space.0'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		pointerEvents: 'none',
	},
});

export type SuggestionCardSourcesProps = {
	sources: SuggestionSource[];
};

// Render nothing if rendering a source throws — most notably when this is mounted
// without an ancestor <SmartCardProvider> (e.g. outside the editor), where the smart
// Card throws. Hoisted so the reference is stable across renders.
const renderNoSources = (): null => null;

/** Skeleton stand-in for the card's title and footer while the URL resolves. */
function SourceSkeleton(): React.JSX.Element {
	return (
		<div css={styles.skeletonOverlay} aria-hidden={true}>
			<Skeleton width="65%" height={12} borderRadius={token('radius.small')} isShimmering />
		</div>
	);
}

function SourceCard({ url }: { url: string }): React.JSX.Element | null {
	const [status, setStatus] = React.useState<'loading' | 'resolved' | 'errored'>('loading');
	const handleResolve = React.useCallback(() => setStatus('resolved'), []);
	const handleError = React.useCallback(() => setStatus('errored'), []);

	if (status === 'errored') {
		return null;
	}

	return (
		<>
			<div css={[status === 'loading' && styles.cardLoading]}>
				<Card
					appearance="inline"
					ui={cardUiOptions}
					url={url}
					onResolve={handleResolve}
					onError={handleError}
				>
					<TitleBlock maxLines={1} />
				</Card>
			</div>
			{status === 'loading' && <SourceSkeleton />}
		</>
	);
}

function SuggestionCardSourcesInner({
	sources,
}: SuggestionCardSourcesProps): React.JSX.Element | null {
	const { formatMessage } = useIntl();

	// The backend supplies the URL, so a source without one can't be linked to and is
	// dropped — even when it has an ARI. De-duplicated so the same URL sent twice doesn't
	// render two identical cards.
	const urls = React.useMemo(() => {
		const unique = new Set<string>();

		for (const { url } of sources) {
			if (url) {
				unique.add(url);
			}
		}

		return Array.from(unique);
	}, [sources]);

	if (urls.length === 0) {
		return null;
	}

	return (
		<div css={styles.sourcesContainer}>
			<Inline xcss={styles.sourceLabel} space="space.100" alignBlock="center">
				<TeamworkGraphIcon label="" size="medium" color={token('color.icon.subtle')} />
				<Text as="span" color="color.text.subtle" size="medium" weight="medium">
					{formatMessage(aiSuggestionsMessages.teamworkGraphLabel)}
				</Text>
			</Inline>
			{urls.map((url) => (
				<div key={url} css={styles.sourceCard}>
					<SourceCard url={url} />
				</div>
			))}
		</div>
	);
}

/** Displays the resolvable sources associated with a suggestion. */
export function SuggestionCardSources({ sources }: SuggestionCardSourcesProps): React.JSX.Element {
	return (
		<ErrorBoundary fallbackRender={renderNoSources}>
			<SuggestionCardSourcesInner sources={sources} />
		</ErrorBoundary>
	);
}
